import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { CoverPicker } from './CoverPicker'
import type { ShareLink, UpdateShareLinkBody } from '../../api/types.gen'
import { WELCOME_MAX } from './types'
import { ApiException, api } from '../../api/client'
import { Banner, Button, Dialog, Textarea } from '../../ui'

interface Props {
  projectId: string
  link: ShareLink
  onClose: () => void
  onSaved: () => void
}

/**
 * Cambiar cómo abre una entrega que ya está repartida.
 *
 * Sólo portada y saludo. Los permisos, el cupo, la contraseña y la caducidad no
 * se editan a propósito: son lo que el enlace autoriza, y cambiárselos por
 * debajo a alguien que ya está dentro convierte una credencial repartida en
 * algo que significa otra cosa cada día. Para eso está anular y crear otro, que
 * sí deja claro que hay que mandar una dirección nueva.
 *
 * Se monta al abrir y se desmonta al cerrar, igual que el diálogo de crear: es
 * lo que garantiza que los campos arranquen con lo que hay guardado y no con lo
 * que se estuviera editando del enlace anterior.
 */
export function EditShareLinkDialog({ projectId, link, onClose, onSaved }: Props) {
  const [coverAssetId, setCoverAssetId] = useState<string | null>(link.cover_asset_id)
  const [welcome, setWelcome] = useState(link.welcome_message ?? '')
  const [failure, setFailure] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ data: ShareLink }, UpdateShareLinkBody>(`/share-links/${link.id}`, {
        cover_asset_id: coverAssetId,
        welcome_message: welcome.trim() === '' ? null : welcome.trim(),
      }),
    onSuccess: () => {
      setFailure(null)
      onSaved()
      onClose()
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo guardar. Revisa tu conexión.',
      )
    },
  })

  return (
    <Dialog
      open
      onClose={onClose}
      title="Cómo abre esta entrega"
      /*
       | La tranquilidad va en la cabecera, no en una ayuda al pie.
       |
       | El miedo razonable al editar algo ya repartido es romperlo: si el
       | estudio no está seguro de que el enlace sigue siendo el mismo, no toca
       | nada y se queda con la errata.
       */
      description={`La dirección no cambia: ${
        link.label ?? 'tu cliente'
      } sigue entrando con el enlace que ya tiene.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            busy={save.isPending && 'Guardando…'}
            onClick={() => {
              setFailure(null)
              save.mutate()
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <CoverPicker
          projectId={projectId}
          value={coverAssetId}
          initialThumbUrl={link.cover_thumb_url}
          onChange={setCoverAssetId}
        />

        <Textarea
          label="Un saludo (opcional)"
          rows={3}
          value={welcome}
          maxLength={WELCOME_MAX}
          onChange={(event) => setWelcome(event.target.value)}
          placeholder="Gracias por dejarnos contar vuestro día. Aquí están vuestras fotos."
          hint="Sale bajo el título de la galería. Lo lee tu cliente, no tú."
        />

        {failure !== null && (
          <Banner tone="danger" title="No se pudo guardar" live="alert">
            {failure}
          </Banner>
        )}
      </div>
    </Dialog>
  )
}
